// RecipeManagement.jsx - Güncellenen Fonksiyonlar
import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaFilter, FaUndoAlt } from 'react-icons/fa';
import api from '../services/api.service';
import RecipeFormModal from './RecipeFormModal';
import './RecipeManagement.css';
import toast from '../services/toast.service'; // Toast servisini import et
import confirmDialog from './ConfirmDialog';

const RecipeManagement = ({ initialRecipeData, onRecipeAction }) => {
  const [recipes, setRecipes] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedMenuItem, setSelectedMenuItem] = useState('');
  
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [recipesRes, menuItemsRes, categoriesRes, ingredientsRes] = await Promise.all([
          api.getRecipes(),
          api.getMenuItems({ isActive: true }),
          api.getMenuCategories({ isActive: true }),
          api.getIngredients({ isActive: true })
        ]);
        
        setRecipes(recipesRes.data);
        setMenuItems(menuItemsRes.data);
        setCategories(categoriesRes.data);
        setIngredients(ingredientsRes.data);
        setError(null);
      } catch (err) {
        console.error("Veri yükleme hatası:", err);
        setError("Veri yüklenirken bir hata oluştu. Lütfen sayfayı yenileyin.");
        toast.error("Veri yüklenirken bir hata oluştu. Lütfen sayfayı yenileyin.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Handle initialRecipeData if provided
  useEffect(() => {
    if (initialRecipeData) {
      handleRecipeAction(initialRecipeData);
    }
  }, [initialRecipeData]);
  
  // Handle recipe selection
  const handleRecipeAction = async (data) => {
    if (!data) return;
    
    try {
      setLoading(true);
      
      if (data.isExisting) {
        // Edit existing recipe
        console.log("Mevcut reçete düzenleniyor, ID:", data.recipeId);
        
        const { data: recipeData } = await api.getRecipe(data.recipeId);
        console.log("API'den gelen reçete verisi:", recipeData);
        
        // API'den gelen veriyi düzenleme modalı için hazırla
        const formattedRecipeData = {
          ...recipeData,
          // Eğer menuItemId yoksa, ama menuItem varsa, menuItemId ekle
          menuItemId: recipeData.menuItemId || (recipeData.menuItem ? recipeData.menuItem.id : null),
          // Eğer ingredients içinde ingredient nesneleri tam değilse formatla
          ingredients: recipeData.ingredients?.map(ing => {
            // RecipeFormModal, ingredients dizisinde her bir elemanın içinde ingredient nesnesi bekliyor
            if (!ing.ingredient && ing.ingredientId) {
              // ingredientId varsa ama ingredient nesnesi yoksa, ingredients listesinden bulup ekle
              const ingredientDetails = ingredients.find(i => 
                i.id.toString() === ing.ingredientId.toString()
              );
              
              if (ingredientDetails) {
                return {
                  ...ing,
                  ingredient: {
                    id: ingredientDetails.id,
                    name: ingredientDetails.name,
                    unit: ingredientDetails.unit,
                    costPerUnit: parseFloat(ingredientDetails.costPerUnit) || 0
                  },
                  quantity: parseFloat(ing.quantity) || 0
                };
              }
            }
            return ing;
          }) || []
        };
        
        console.log("Düzenleme için hazırlanan reçete verisi:", formattedRecipeData);
        setSelectedRecipe(formattedRecipeData);
      } else {
        // Create new recipe
        const menuItemId = data.menuItemId;
        const menuItem = menuItems.find(item => item.id.toString() === menuItemId.toString());
        
        if (!menuItem) {
          throw new Error("Menü öğesi bulunamadı");
        }
        
        const newRecipe = {
          menuItemId,
          menuItem: menuItem, // Menü öğesinin tamamını da ekle
          description: '',
          preparationInstructions: '',
          preparationTime: '',
          portionSize: '',
          costPerServing: '',
          ingredients: []
        };
        
        console.log("Yeni reçete oluşturuluyor:", newRecipe);
        setSelectedRecipe(newRecipe);
      }
      
      setShowRecipeModal(true);
      if (onRecipeAction) onRecipeAction();
      setError(null);
    } catch (err) {
      console.error("Reçete işleme hatası:", err);
      setError("Reçete işlenirken bir hata oluştu. Hata: " + (err.message || err));
      toast.error("Reçete işlenirken bir hata oluştu: " + (err.message || err));
    } finally {
      setLoading(false);
    }
  };
  
  // Filter recipes
  const filteredRecipes = recipes.filter(recipe => {
    if (selectedCategory && recipe.menuItem.category.id.toString() !== selectedCategory) {
      return false;
    }
    if (selectedMenuItem && recipe.menuItem.id.toString() !== selectedMenuItem) {
      return false;
    }
    return true;
  });
  
  // Reset filters
  const resetFilters = () => {
    setSelectedCategory('');
    setSelectedMenuItem('');
  };
  
  // Handle new recipe creation
  const handleNewRecipe = () => {
    setSelectedRecipe({
      menuItemId: '',
      description: '',
      preparationInstructions: '',
      preparationTime: '',
      portionSize: '',
      costPerServing: '',
      ingredients: []
    });
    setShowRecipeModal(true);
  };
  
  // Delete recipe - Toast kullanan versiyon
  const handleDeleteRecipe = async (recipeId, recipeName) => {
    // window.confirm yerine kendi oluşturduğumuz confirm dialog kullanabiliriz
    // Ancak şimdilik basit alert kullanacağız
    const confirmed = await confirmDialog.delete(
        `"${recipeName}" reçetesini silmek istediğinizden emin misiniz?`, 
        {
          title: 'Reçete Silme',
          confirmText: 'Evet, Sil',
          cancelText: 'Vazgeç'
        }
      );
      
      // Eğer kullanıcı onaylamazsa, işlemi sonlandır
      if (!confirmed) {
        return;
      }
    
    try {
      setLoading(true);
      await api.deleteRecipe(recipeId);
      setRecipes(recipes.filter(recipe => recipe.id !== recipeId));
      setError(null);
      
      // Alert yerine toast kullan
      toast.success('Reçete başarıyla silindi.');
    } catch (err) {
      console.error("Reçete silme hatası:", err);
      setError("Reçete silinirken bir hata oluştu: " + (err.message || err));
      
      // Hata bildirimi için toast.error kullan
      toast.error("Reçete silinirken bir hata oluştu: " + (err.message || err));
    } finally {
      setLoading(false);
    }
  };
  
  // Save recipe - Toast kullanan versiyon
  const handleSaveRecipe = async (recipe) => {
    try {
      setLoading(true);
      console.log("Gönderilecek reçete verisi:", recipe); // Debug için veriyi logla
      
      // Recipe ID kontrolü
      let recipeToSave = { ...recipe };
      
      // Yeni recipe ID'si null veya undefined olacak, var olan recipe için ID olacak
      const isNewRecipe = !recipeToSave.id;
      
      // API'ye gönderilecek veriyi hazırla
      if (isNewRecipe) {
        // Yeni reçete: menuItemId string olarak gönderildiğinden emin ol
        recipeToSave.menuItemId = recipeToSave.menuItemId.toString();
      } else {
        // Mevcut reçete: ID'nin string olduğundan emin ol
        recipeToSave.id = recipeToSave.id.toString();
      }
      
      // Malzemeler için format düzeltmesi
      if (recipeToSave.ingredients && Array.isArray(recipeToSave.ingredients)) {
        recipeToSave.ingredients = recipeToSave.ingredients.map(ing => ({
          ...ing,
          ingredientId: ing.ingredientId?.toString(),
          quantity: parseFloat(ing.quantity) || 0
        }));
      }
      
      const { data } = await api.createOrUpdateRecipe(recipeToSave);
      
      // Başarılı API yanıtı sonrası UI güncelle
      console.log("API yanıtı:", data); // Debug için API yanıtını logla
      
      // Update recipes list
      const recipeIndex = recipes.findIndex(r => r.id === data.id);
      if (recipeIndex >= 0) {
        const updatedRecipes = [...recipes];
        updatedRecipes[recipeIndex] = data;
        setRecipes(updatedRecipes);
      } else {
        setRecipes([...recipes, data]);
      }
      
      setShowRecipeModal(false);
      setError(null);
      
      // Alert yerine toast kullan
      toast.success(isNewRecipe ? 'Reçete başarıyla oluşturuldu.' : 'Reçete başarıyla güncellendi.');
    } catch (err) {
      console.error("Reçete kaydetme hatası:", err);
      setError("Reçete kaydedilirken bir hata oluştu: " + (err.message || err));
      
      // Alert yerine toast.error kullan
      toast.error("Reçete kaydedilemedi. Hata: " + (err.message || err));
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="recipe-management">
      {error && <div className="error-message">{error}</div>}
      
      <div className="filter-panel">
        <div className="filter-group">
          <label className="filter-label" htmlFor="category-filter">Kategori Seçin</label>
          <select
            id="category-filter"
            className="filter-select"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            disabled={loading}
          >
            <option value="">Tüm Kategoriler</option>
            {categories.map(category => (
              <option key={category.id} value={category.id.toString()}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
        
        <div className="filter-group">
          <label className="filter-label" htmlFor="menu-item-filter">Menü Öğesi Seçin</label>
          <select
            id="menu-item-filter"
            className="filter-select"
            value={selectedMenuItem}
            onChange={(e) => setSelectedMenuItem(e.target.value)}
            disabled={loading}
          >
            <option value="">Tüm Menü Öğeleri</option>
            {menuItems.map(menuItem => (
              <option key={menuItem.id} value={menuItem.id.toString()}>
                {menuItem.name}
              </option>
            ))}
          </select>
        </div>
        
        <button 
          className="reset-button"
          onClick={resetFilters}
          disabled={(!selectedCategory && !selectedMenuItem) || loading}
        >
          <FaUndoAlt className="button-icon" />
          Filtreleri Temizle
        </button>
        
        <button 
          className="add-button"
          onClick={handleNewRecipe}
          disabled={loading}
        >
          <FaPlus className="button-icon" />
          Yeni Reçete
        </button>
      </div>
      
      {loading && !showRecipeModal ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Veriler yükleniyor...</p>
        </div>
      ) : filteredRecipes.length > 0 ? (
        <div className="recipe-table-container">
          <table className="recipe-table">
            <thead>
              <tr>
                <th>Menü Öğesi</th>
                <th>Kategori</th>
                <th>Porsiyon</th>
                <th>Hazırlama Süresi</th>
                <th>Maliyet</th>
                <th>Satış Fiyatı</th>
                <th>Kar Marjı</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecipes.map(recipe => {
                // Ensure both cost and price are valid numbers
                const cost = parseFloat(recipe.calculatedCost) || 0;
                const price = parseFloat(recipe.menuItem.price) || 0;
                const margin = price - cost;
                const percentage = price > 0 ? (margin / price) * 100 : 0;
                
                return (
                  <tr key={recipe.id}>
                    <td>{recipe.menuItem.name}</td>
                    <td>{recipe.menuItem.category?.name || 'Kategorisiz'}</td>
                    <td>{recipe.portionSize || '-'}</td>
                    <td>
                      {recipe.preparationTime 
                        ? `${recipe.preparationTime} dakika` 
                        : '-'
                      }
                    </td>
                    <td>₺{cost.toFixed(2)}</td>
                    <td>₺{price.toFixed(2)}</td>
                    <td>
                      <span className={`profit-badge ${margin > 0 ? 'positive' : 'negative'}`}>
                        ₺{margin.toFixed(2)} (%{percentage.toFixed(2)})
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="edit-button"
                          onClick={() => handleRecipeAction({ recipeId: recipe.id, isExisting: true })}
                          disabled={loading}
                          aria-label={`${recipe.menuItem.name} reçetesini düzenle`}
                        >
                          <FaEdit className="button-icon" />
                          Düzenle
                        </button>
                        <button 
                          className="delete-button"
                          onClick={() => handleDeleteRecipe(recipe.id, recipe.menuItem.name)}
                          disabled={loading}
                          aria-label={`${recipe.menuItem.name} reçetesini sil`}
                        >
                          <FaTrash className="button-icon" />
                          Sil
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="info-message">
          {selectedCategory || selectedMenuItem 
            ? 'Seçilen filtrelere uygun reçete bulunamadı.' 
            : 'Henüz reçete eklenmemiş. "Yeni Reçete" butonu ile reçete ekleyebilirsiniz.'}
        </div>
      )}
      
      {/* Recipe Modal */}
      {showRecipeModal && selectedRecipe && (
        <RecipeFormModal 
          recipe={selectedRecipe}
          menuItems={menuItems}
          ingredients={ingredients}
          onClose={() => setShowRecipeModal(false)}
          onSave={handleSaveRecipe}
          isLoading={loading}
        />
      )}
    </div>
  );
};

export default RecipeManagement;